import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ItemFull } from '@lr/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { addToGuestBasket } from '../lib/guestBasket';

export function ItemDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const [accOpen, setAccOpen] = useState<{ [k: string]: boolean }>({ desc: true });
  const [fitsOpen, setFitsOpen] = useState(false);
  const [accReturns, setAccReturns] = useState<string | null>(null);
  const [accDamage, setAccDamage] = useState<string | null>(null);

  // Pull admin-set accordion copy (editable in /admin/site-content). Falls back
  // to the hard-coded defaults below when nothing's been published yet.
  useEffect(() => {
    apiFetch<Record<string, { body?: string }>>('/api/catalog/site-content')
      .then((all) => {
        if (all.pdp_returns_rotation?.body) setAccReturns(all.pdp_returns_rotation.body);
        if (all.pdp_damage_loss?.body) setAccDamage(all.pdp_damage_loss.body);
      })
      .catch(() => {
        // ignore
      });
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    // Always open an item page at the top — otherwise the storefront
    // scroll container would inherit whatever offset Browse was at.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    apiFetch<ItemFull>(`/api/catalog/items/${slug}`)
      .then((it) => {
        setItem(it);
        setActiveImg(0);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const [added, setAdded] = useState(false);

  async function addToBasket() {
    if (!item) return;
    // Guest flow: no signup gate — items go into localStorage and are
    // merged into the server basket the first time they sign in.
    if (!user) {
      addToGuestBasket(item.id);
      setAdded(true);
      return;
    }
    setAdding(true);
    setError(null);
    try {
      await apiFetch('/api/basket/add', {
        method: 'POST',
        body: JSON.stringify({ item_id: item.id })
      });
      setAdded(true);
      // Notify the layout that the basket count changed.
      window.dispatchEvent(new CustomEvent('lr:basket-changed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reserve');
    } finally {
      setAdding(false);
    }
  }

  if (loading) {
    return (
      <section className="screen">
        <div className="scroll" style={{ padding: '40px 22px', color: 'var(--ink-soft)' }}>
          Loading…
        </div>
      </section>
    );
  }

  if (error && !item) {
    return (
      <section className="screen">
        <div className="scroll empty-grid">
          <div className="h">Couldn&apos;t find this piece</div>
          <div className="p">{error}</div>
        </div>
      </section>
    );
  }
  if (!item) return null;

  // Combine the main image with the gallery so customers see everything.
  // Main comes first; any gallery row that points at the same URL is filtered
  // out so we don't display the same photo twice.
  const gallery = (item.images ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const images = [
    ...(item.main_image_url
      ? [
          {
            id: 'main',
            item_id: item.id,
            url: item.main_image_url,
            alt_text: null,
            sort_order: -1
          }
        ]
      : []),
    ...gallery.filter((img) => img.url !== item.main_image_url)
  ];
  const damageFee = (item.damage_fee_pence / 100).toFixed(0);
  const soldOut = item.status !== 'available';

  return (
    <section className="screen">
      <div className="scroll">
        <div className="pd-layout">
          <div className="pd-col-left">
            <div
              className="pd-img"
              style={
                images[activeImg]
                  ? { backgroundImage: `url(${images[activeImg].url})` }
                  : undefined
              }
              onTouchStart={(e) => {
                touchStartXRef.current = e.touches[0].clientX;
                touchStartYRef.current = e.touches[0].clientY;
              }}
              onTouchEnd={(e) => {
                const sx = touchStartXRef.current;
                const sy = touchStartYRef.current;
                if (sx == null || sy == null || images.length < 2) return;
                const ex = e.changedTouches[0].clientX;
                const ey = e.changedTouches[0].clientY;
                const dx = ex - sx;
                const dy = ey - sy;
                // Treat as horizontal swipe only when X movement is dominant
                // and big enough to count as a deliberate gesture.
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                  if (dx < 0) setActiveImg((i) => Math.min(i + 1, images.length - 1));
                  else setActiveImg((i) => Math.max(i - 1, 0));
                }
                touchStartXRef.current = null;
                touchStartYRef.current = null;
              }}
            />
            {images.length > 1 && (
              <>
                <div className="pd-dots">
                  {images.map((img, i) => (
                    <span
                      key={img.id}
                      className={i === activeImg ? 'on' : undefined}
                      onClick={() => setActiveImg(i)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
                <div className="pd-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={img.id}
                      type="button"
                      className={`pd-thumb${i === activeImg ? ' on' : ''}`}
                      aria-label={`Image ${i + 1} of ${images.length}`}
                      onClick={() => setActiveImg(i)}
                      style={{ backgroundImage: `url(${img.url})` }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="pd-col-right">
            {/* CTA sits right after the image on mobile (because pd-col-right
                comes straight after pd-col-left in the source order) and at
                the top of the right column on desktop. */}
            <div className="pd-cta-inline">
              {added ? (
                <AddedActions onBrowseMore={() => navigate('/browse')} />
              ) : (
                <>
                  <button
                    className={`cta-full${soldOut ? ' disabled' : ''}`}
                    disabled={soldOut || adding}
                    onClick={() => void addToBasket()}
                  >
                    <span>
                      {soldOut ? 'OUT OF ROTATION' : adding ? 'RESERVING…' : 'ADD TO BAG'}
                    </span>
                    <Arrow />
                  </button>
                  <div className="micro" style={{ paddingTop: 10 }}>
                    <Clock />
                    Wear for 30 days. Prepaid return label included.
                  </div>
                </>
              )}
              {error && (
                <div className="form-error" style={{ marginTop: 12 }}>
                  {error}
                </div>
              )}
            </div>

            <div className="pd-info">
              <div className="brand-name">{item.brand?.name ?? '—'}</div>
              <h1>{item.name}</h1>
              <div className="pd-meta">
                <div className="credits-big">
                  {item.credit_price} {item.credit_price === 1 ? 'CREDIT' : 'CREDITS'}
                </div>
              </div>
              <div className="pd-row">
                <span className="lbl">Condition</span>
                <span className="val">{labelCondition(item.condition)}</span>
              </div>
              {item.category?.name && (
                <div className="pd-row">
                  <span className="lbl">Category</span>
                  <span className="val">{item.category.name}</span>
                </div>
              )}
              {(item.recommended_size || item.label_size) && (
                <div className="fits-block">
                  <div className="fits-grid">
                    {item.label_size && (
                      <div className="fits-col">
                        <div className="label">Label size</div>
                        <div className="size-val">{item.label_size}</div>
                      </div>
                    )}
                    {item.recommended_size && (
                      <div className="fits-col">
                        <div className="label">
                          Fits like{' '}
                          <button
                            className="info-btn"
                            onClick={() => setFitsOpen((v) => !v)}
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: 'var(--bg-3)',
                              color: 'var(--ink-soft)',
                              fontSize: 10,
                              fontWeight: 700
                            }}
                          >
                            i
                          </button>
                        </div>
                        <div className="size-val">{item.recommended_size}</div>
                      </div>
                    )}
                  </div>
                  {fitsOpen && (
                    <div
                      style={{
                        marginTop: 8,
                        background: 'var(--ink)',
                        color: '#fff',
                        padding: '10px 14px',
                        borderRadius: 10,
                        fontSize: 12,
                        lineHeight: 1.4
                      }}
                    >
                      Fits like is based on our own measurements, not the original label.
                    </div>
                  )}
                </div>
              )}
            </div>

            {(item.pit_to_pit_cm || item.shoulder_width_cm || item.length_cm) && (
              <div className="measurements">
                <div className="h">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  >
                    <path d="M2 8h20v8H2z" />
                    <path d="M6 8v3M10 8v3M14 8v3M18 8v3" />
                  </svg>
                  MEASUREMENTS
                </div>
                {item.pit_to_pit_cm && (
                  <div className="row">
                    <span>Pit to pit</span>
                    <span className="v">{item.pit_to_pit_cm} cm</span>
                  </div>
                )}
                {item.shoulder_width_cm && (
                  <div className="row">
                    <span>Shoulder width</span>
                    <span className="v">{item.shoulder_width_cm} cm</span>
                  </div>
                )}
                {item.length_cm && (
                  <div className="row">
                    <span>Length</span>
                    <span className="v">{item.length_cm} cm</span>
                  </div>
                )}
              </div>
            )}

            {item.description && (
              <Accordion
                id="desc"
                title="DESCRIPTION"
                open={accOpen.desc !== false}
                onToggle={() => setAccOpen((s) => ({ ...s, desc: !s.desc }))}
              >
                <p>{item.description}</p>
              </Accordion>
            )}

            <Accordion
              id="returns"
              title="RETURNS & ROTATION PERIOD"
              open={!!accOpen.returns}
              onToggle={() => setAccOpen((s) => ({ ...s, returns: !s.returns }))}
            >
              {accReturns ? (
                <Paragraphs text={accReturns.replace(/\{damage_fee\}/g, damageFee)} />
              ) : (
                <p>
                  Your rotation lasts 30 days from delivery. We email a prepaid return label
                  between Day 25–30. A 2-day grace period applies. Late fees of £10 per day
                  apply from Day 33.
                </p>
              )}
            </Accordion>
            <Accordion
              id="damage"
              title="DAMAGE & LOSS"
              open={!!accOpen.damage}
              onToggle={() => setAccOpen((s) => ({ ...s, damage: !s.damage }))}
            >
              {accDamage ? (
                <Paragraphs text={accDamage.replace(/\{damage_fee\}/g, damageFee)} />
              ) : (
                <p>
                  Normal wear is expected and never charged. A damage fee only applies if the
                  item cannot be returned to the rotation. The damage fee for this item is
                  £{damageFee}.
                </p>
              )}
            </Accordion>

            <div style={{ height: 16 }} />
          </div>
        </div>
      </div>

    </section>
  );
}

/** Render a string with blank-line-separated paragraphs. */
function Paragraphs({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/\n\s*\n/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((p, i) => (
          <p key={i}>{p}</p>
        ))}
    </>
  );
}

function AddedActions({ onBrowseMore }: { onBrowseMore: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 16px',
          background: 'var(--good-bg)',
          color: 'var(--good)',
          borderRadius: 'var(--radius)',
          fontSize: 13,
          fontWeight: 600
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Added to your bag
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button
          type="button"
          className="cta-full"
          onClick={onBrowseMore}
          style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)' }}
        >
          <span>KEEP BROWSING</span>
        </button>
        <Link to="/checkout" className="cta-full">
          <span>GO TO BAG</span>
          <Arrow />
        </Link>
      </div>
    </div>
  );
}

function Accordion({
  title,
  open,
  onToggle,
  children
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="accordion">
      <button className={`acc-head${open ? ' open' : ''}`} onClick={onToggle}>
        <span className="l">{title}</span>
        <span className="chev">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div className={`acc-body${open ? ' open' : ''}`}>
        <div className="inner">{children}</div>
      </div>
    </div>
  );
}

function labelCondition(c: string): string {
  return c
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function Arrow() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function Clock() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
